// src/features/admin/components/settings/integrations/components/DocumentStorageTab.tsx
import React, { useState } from 'react';
import {
  ServerIcon,
  CloudIcon,
  FolderIcon,
  CheckCircleIcon,
  Cog6ToothIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { getStorageIcon } from '@/components/icons/techIconMap';

interface StorageProvider {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  status: 'connected' | 'disconnected' | 'error';
  isActive: boolean;
  costPerGB: string;
}

const STORAGE_PROVIDERS: StorageProvider[] = [
  {
    id: 'gcs',
    name: 'Google Cloud Storage',
    description: 'Primary document storage. Recommended for DMS documents with automatic versioning and compliance features.',
    icon: getStorageIcon('gcs') ?? CloudIcon,
    status: 'connected',
    isActive: true,
    costPerGB: '$0.020/GB/month'
  },
  {
    id: 'local',
    name: 'Local Server',
    description: 'Store documents on local server disk. Good for development or air-gapped environments.',
    icon: ServerIcon,
    status: 'disconnected',
    isActive: false,
    costPerGB: 'Free (disk only)'
  },
  {
    id: 's3',
    name: 'Amazon S3',
    description: 'AWS object storage for firms with existing AWS infrastructure.',
    icon: getStorageIcon('s3') ?? CloudIcon,
    status: 'disconnected',
    isActive: false,
    costPerGB: '$0.023/GB/month'
  },
  {
    id: 'azure',
    name: 'Azure Blob Storage',
    description: 'Microsoft cloud storage for Microsoft 365 environments.',
    icon: getStorageIcon('azure') ?? CloudIcon,
    status: 'disconnected',
    isActive: false,
    costPerGB: '$0.018/GB/month'
  },
  {
    id: 'gdrive',
    name: 'Google Drive',
    description: 'Use Google Drive for document storage with familiar sharing capabilities.',
    icon: getStorageIcon('gdrive') ?? FolderIcon,
    status: 'disconnected',
    isActive: false,
    costPerGB: 'Included in Workspace'
  },
  {
    id: 'onedrive',
    name: 'OneDrive / SharePoint',
    description: 'Microsoft 365 document storage with SharePoint integration.',
    icon: getStorageIcon('onedrive') ?? FolderIcon,
    status: 'disconnected',
    isActive: false,
    costPerGB: 'Included in M365'
  },
  {
    id: 'dropbox',
    name: 'Dropbox Business',
    description: 'Dropbox Business integration for teams already using Dropbox.',
    icon: FolderIcon,
    status: 'disconnected',
    isActive: false,
    costPerGB: 'Included in plan'
  }
];

const DocumentStorageTab: React.FC = () => {
  const [providers] = useState<StorageProvider[]>(STORAGE_PROVIDERS);
  const activeProvider = providers.find(p => p.isActive);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <FolderIcon className="h-6 w-6 text-lex-accent mr-2" />
          Document Storage
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Configure where DMS documents are stored. This is separate from email storage.
        </p>
      </div>

      {/* Active Provider */}
      {activeProvider && (
        <div className="bg-lex-accent-soft border border-lex-accent/30 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-lex-accent mr-2" />
            <span className="text-sm font-medium text-lex-accent">
              Active Storage: <strong>{activeProvider.name}</strong>
            </span>
          </div>
        </div>
      )}

      {/* Provider Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {providers.map((provider) => {
          const Icon = provider.icon;
          return (
            <div
              key={provider.id}
              className={`bg-white dark:bg-gray-800 rounded-lg border p-4 ${
                provider.isActive
                  ? 'border-lex-accent ring-2 ring-lex-accent/20'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <div className={`p-2 rounded-lg ${
                    provider.isActive ? 'bg-lex-accent-soft' : 'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    <Icon className={`h-5 w-5 ${
                      provider.isActive ? 'text-lex-accent' : 'text-gray-500'
                    }`} />
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {provider.name}
                    </h4>
                    <span className={`text-xs ${
                      provider.status === 'connected' ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      {provider.status === 'connected' ? '● Connected' : '○ Not configured'}
                    </span>
                  </div>
                </div>
                {provider.isActive && (
                  <span className="text-xs font-medium text-lex-accent bg-lex-accent-soft px-2 py-0.5 rounded">
                    Active
                  </span>
                )}
              </div>
              
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                {provider.description}
              </p>
              
              <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                <span className="text-xs text-gray-500">{provider.costPerGB}</span>
                <div className="flex space-x-2">
                  <button className="text-xs text-gray-600 hover:text-gray-800 dark:text-gray-400">
                    <Cog6ToothIcon className="h-4 w-4" />
                  </button>
                  {!provider.isActive && (
                    <button className="text-xs text-lex-accent hover:text-lex-accent-hover font-medium">
                      Activate
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DocumentStorageTab;
